// created by NZ2GVZ on 4/2021 the global theme file  using material ui that handles
// global theme for dark and light mode
// the fonts to override the material ui

import { createTheme, type Theme } from '@mui/material';
import { createBreakpoints } from '@mui/system';

import type { BDThemeExtensions } from './BDTheme.types';
import { CS_BREAKPOINTS } from './constants/breakpoints.constant';
import {
  FONT_STACK,
  interBold,
  interFontFace,
  interLight,
  interMedium,
  interRegular,
  interSemibold,
} from './constants/fontface.constant';
import {
  COLOR_PALETTE,
  DESIGN_SYSTEM_COLORS,
  designSystemColors,
} from './constants/palette.constant';
import { NEW_SHAPES, NEW_SPACING } from './constants/spacing.constants';
import { DESIGN_SYSTEM_TYPOGRAPHY_TOKENS } from './constants/typography.constants';
import type { BDFontFace } from './models/fonts.model';
import getMuiOverrides from './overrides';

declare module '@mui/material/styles' {
  interface PaletteColor {
    lighter?: string;
    300?: string;
  }

  interface SimplePaletteColorOptions {
    lighter?: string;
  }
  interface TypeText {
    tertiary?: string;
  }

  interface TypeObject {
    tertiary?: string;
  }

  /* Deprecate Outdated but In-Use Typography Variants */
  interface DeprecatedTypographyVariantsOptions {
    body1_deprecated: React.CSSProperties;
    button_deprecated: React.CSSProperties;
    h1_deprecated: React.CSSProperties;
    h2_deprecated: React.CSSProperties;
    h3_deprecated: React.CSSProperties;
    h4_deprecated: React.CSSProperties;
    h5_deprecated: React.CSSProperties;
    caption_regular_deprecated: React.CSSProperties;
    caption_medium_deprecated: React.CSSProperties;
    body_medium_deprecated: React.CSSProperties;
    header1_medium_deprecated: React.CSSProperties;
    label_regular_deprecated: React.CSSProperties;
  }

  /* BrightDrop design-system typography variants (plus non-DS extras) */
  interface CustomTypographyVariants {
    button_new: React.CSSProperties;

    oversize1_bold: React.CSSProperties;
    oversize1_light: React.CSSProperties;
    oversize2_regular: React.CSSProperties;
    oversize3_medium: React.CSSProperties;
    display1_semibold: React.CSSProperties;
    display1_medium: React.CSSProperties;
    display1_regular: React.CSSProperties;
    display2_semibold: React.CSSProperties;
    display2_medium: React.CSSProperties;
    display2_regular: React.CSSProperties;
    header1_medium: React.CSSProperties;
    header1_regular: React.CSSProperties;
    header2_semibold: React.CSSProperties;
    header2_medium: React.CSSProperties;
    header2_regular: React.CSSProperties;
    body_medium: React.CSSProperties;
    body_regular: React.CSSProperties;
    body_italic: React.CSSProperties;
    label_medium: React.CSSProperties;
    label_regular: React.CSSProperties;
    label_small: React.CSSProperties;
    caption_medium: React.CSSProperties;
    caption_regular: React.CSSProperties;
    overline_semibold: React.CSSProperties;
    small_body_medium: React.CSSProperties;
    small_body_regular: React.CSSProperties;

    // non-design system typography
    input_floating_label_regular: React.CSSProperties;
  }

  // TypographyVariants and TypographyVariantsOptions each add the custom
  // variants independently. TypographyVariantsOptions must not be a base of
  // TypographyVariants: doing so would make MUI's `Typography` inherit
  // `TypographyOptions`, whose optional standard-variant and font bases are not
  // identical to `Typography`'s own required ones.
  interface TypographyVariantsOptions
    extends DeprecatedTypographyVariantsOptions,
      CustomTypographyVariants {}

  interface TypographyVariants
    extends DeprecatedTypographyVariantsOptions,
      CustomTypographyVariants {}
}

declare module '@mui/material/Typography' {
  /* Disable default Typography Variants */
  interface DisabledTypographyVariantOverrides {
    body1: false;
    body2: false;
    button: false;
    h1: false;
    h2: false;
    h3: false;
    h4: false;
    h5: false;
    subtitle1: false;
    subtitle2: false;
  }

  /* Deprecate Outdated but In-Use Typography Variants */
  interface DeprecatedTypographyVariantOverrides {
    body1_deprecated: true;
    button_deprecated: true;
    h1_deprecated: true;
    h2_deprecated: true;
    h3_deprecated: true;
    h4_deprecated: true;
    h5_deprecated: true;
    caption_regular_deprecated: true;
    caption_medium_deprecated: true;
    body_medium_deprecated: true;
    header1_medium_deprecated: true;
    label_regular_deprecated: true;
  }

  interface TypographyPropsVariantOverrides
    extends DisabledTypographyVariantOverrides,
      DeprecatedTypographyVariantOverrides {
    button_new: true;

    oversize1_bold: true;
    oversize1_light: true;
    oversize2_regular: true;
    oversize3_medium: true;
    display1_semibold: true;
    display1_medium: true;
    display1_regular: true;
    display2_semibold: true;
    display2_medium: true;
    display2_regular: true;
    header1_medium: true;
    header1_regular: true;
    header2_semibold: true;
    header2_medium: true;
    header2_regular: true;
    body_medium: true;
    body_regular: true;
    body_italic: true;
    label_medium: true;
    label_regular: true;
    label_small: true;
    caption_medium: true;
    caption_regular: true;
    overline_semibold: true;
    small_body_medium: true;
    small_body_regular: true;
    input_floating_label_regular: true;
  }
}

// custom  bright drop color codes declaration
declare module '@mui/material/styles/createTheme' {
  interface Theme extends BDThemeExtensions {}
  interface ThemeOptions extends BDThemeExtensions {}
}

declare module '@mui/material/styles/createPalette' {
  interface Palette {
    svgIcon: Palette['primary']; // TODO:remove when the color palette US completed
    label: Palette['primary']; // TODO:remove when the color palette US completed
    brand: Palette['primary'];
    border: string;
    inputBackground: string;
    inputBoxshadow: string;
  }
  interface PaletteOptions {
    svgIcon: PaletteOptions['primary']; // TODO:remove when the color palette US completed
    label: PaletteOptions['primary']; // TODO:remove when the color palette US completed
    brand: PaletteOptions['primary'];
    border: string;
    inputBackground: string;
    inputBoxshadow: string;
  }
}
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    brand: true;
  }
}

const { size: FONT_SIZE, lineHeight: LINE_HEIGHT } =
  DESIGN_SYSTEM_TYPOGRAPHY_TOKENS;

/**
 * Builds a typography variant. Inter is a variable font, so `font` contributes
 * only its weight — every variant shares `FONT_STACK`.
 */
const fontVariant = (
  font: BDFontFace,
  fontSize: React.CSSProperties['fontSize'],
  lineHeight: React.CSSProperties['lineHeight']
): React.CSSProperties => ({
  fontFamily: FONT_STACK,
  fontSize,
  lineHeight,
  fontWeight: font.fontWeight,
});

const createBDTheme = (): Theme => {
  const newDesignSystemColors = designSystemColors();

  const breakpoints = createBreakpoints({
    values: CS_BREAKPOINTS,
  });

  const createCustomTheme = () => {
    const defaultTheme = createTheme({
      breakpoints,
      // TODO:remove when the color palette US completed
      bdColors: {
        sky80: COLOR_PALETTE.common.sky80,
        lightBlue: COLOR_PALETTE.dark.lightBlue,
        darkBlue: COLOR_PALETTE.common.darkBlue,
        pattensBlue: COLOR_PALETTE.light.pattensBlue,
        magnolia: COLOR_PALETTE.light.magnolia,
        orange: COLOR_PALETTE.common.orange,
        darkGray: COLOR_PALETTE.light.darkGray,
        oxfordBlue: COLOR_PALETTE.light.oxfordBlue,
        paleBlue: COLOR_PALETTE.common.paleBlue,
        bodyBgColor: COLOR_PALETTE.light.bodyBgColor,
        dimGray: COLOR_PALETTE.common.dimGray,
        alabaster: COLOR_PALETTE.common.alabaster,
        smokyGrey: COLOR_PALETTE.common.smokyGrey,
        eucalyptus: COLOR_PALETTE.common.eucalyptus,
        black5: COLOR_PALETTE.common.black5,
        navy10: COLOR_PALETTE.common.navy10,
      },
      // TODO:remove when the color palette US completed
      battery: {
        low: COLOR_PALETTE.common.error,
        medium: COLOR_PALETTE.common.warning,
        high: COLOR_PALETTE.common.success,
        unknown: COLOR_PALETTE.common.mediumGrey,
      },
      palette: {
        mode: 'light',

        // TODO:remove when the color palette US completed
        label: {
          main: COLOR_PALETTE.light.oxfordBlue,
          dark: COLOR_PALETTE.light.oxfordBlue,
          light: COLOR_PALETTE.common.brandColor1,
        },
        text: {
          primary: DESIGN_SYSTEM_COLORS.text.primary,
          secondary: DESIGN_SYSTEM_COLORS.text.secondary,
          tertiary: DESIGN_SYSTEM_COLORS.text.tertiary,
          disabled: DESIGN_SYSTEM_COLORS.text.tertiary,
        },
        // TODO:remove when the color palette US completed
        primary: {
          light: COLOR_PALETTE.common.brandColor3,
          main: COLOR_PALETTE.common.brandColor1,
          dark: COLOR_PALETTE.common.tangaroa,
          contrastText: COLOR_PALETTE.common.white,
        },
        error: {
          dark: DESIGN_SYSTEM_COLORS.danger[800],
          main: DESIGN_SYSTEM_COLORS.danger[600],
          light: DESIGN_SYSTEM_COLORS.danger[200],
          lighter: DESIGN_SYSTEM_COLORS.danger[100],
        },
        warning: {
          dark: DESIGN_SYSTEM_COLORS.warning[800],
          main: DESIGN_SYSTEM_COLORS.warning[600],
          light: DESIGN_SYSTEM_COLORS.warning[200],
          lighter: DESIGN_SYSTEM_COLORS.warning[100],
        },
        success: {
          dark: DESIGN_SYSTEM_COLORS.success[800],
          main: DESIGN_SYSTEM_COLORS.success[600],
          light: DESIGN_SYSTEM_COLORS.success[200],
          lighter: DESIGN_SYSTEM_COLORS.success[100],
        },
        brand: {
          dark: DESIGN_SYSTEM_COLORS.brand[800],
          main: DESIGN_SYSTEM_COLORS.brand[600],
          light: DESIGN_SYSTEM_COLORS.brand[200],
          lighter: DESIGN_SYSTEM_COLORS.brand[100],
          300: DESIGN_SYSTEM_COLORS.brand[300],
        },
        background: {
          default: DESIGN_SYSTEM_COLORS.common.white,
          paper: DESIGN_SYSTEM_COLORS.common.white,
        },
        divider: `${DESIGN_SYSTEM_COLORS.text.primary}14`,
        border: `${DESIGN_SYSTEM_COLORS.text.primary}33`,
        inputBackground: `${DESIGN_SYSTEM_COLORS.text.primary}05`,
        inputBoxshadow: `${DESIGN_SYSTEM_COLORS.brand[600]}30`,
        grey: {
          100: DESIGN_SYSTEM_COLORS.grey[100],
          200: DESIGN_SYSTEM_COLORS.grey[200],
          300: DESIGN_SYSTEM_COLORS.grey[300],
          500: DESIGN_SYSTEM_COLORS.grey[500],
          700: DESIGN_SYSTEM_COLORS.grey[700],
          800: DESIGN_SYSTEM_COLORS.grey[800],
          900: DESIGN_SYSTEM_COLORS.grey[900],
        },
        common: {
          white: DESIGN_SYSTEM_COLORS.common.white,
        },
        // TODO:remove when the color palette US completed
        svgIcon: {
          main: COLOR_PALETTE.light.oxfordBlue,
          light: COLOR_PALETTE.light.fuscousGrey,
          dark: COLOR_PALETTE.light.oxfordBlue,
          contrastText: COLOR_PALETTE.light.navyBlue,
        },
      },
      new: {
        boxShadows: {
          elevation1: '0px 1px 12px 0px #1D2A340F',
          elevation2: '0px 4px 12px 0px #0C11141A',
        },
        color: newDesignSystemColors,
        spacing: NEW_SPACING,
        shape: NEW_SHAPES,
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            '@font-face': interFontFace,
            /* autofill color is rendering in chrome to resolve that added the below code */
            '.MuiOutlinedInput-input:-webkit-autofill, .form-control:-webkit-autofill':
              {
                boxShadow: `0 0 0 100px ${DESIGN_SYSTEM_COLORS.common.white} inset !important`,
                textFillColor: DESIGN_SYSTEM_COLORS.text.primary,
              },
            '.MuiPopover-paper.bdSelectDropDownPaper': {
              boxShadow: 'none',
              borderRadius: '0',
              border: `1px solid ${DESIGN_SYSTEM_COLORS.text.primary}33`,
            },
            body: {
              /* to reduce scrolling */
              minHeight: 'calc(100vh - 80px)',
              background: DESIGN_SYSTEM_COLORS.grey[100],

              /* use body_regular typography styling as the default style */
              fontFamily: FONT_STACK,
              fontSize: DESIGN_SYSTEM_TYPOGRAPHY_TOKENS.size.body,
              lineHeight: DESIGN_SYSTEM_TYPOGRAPHY_TOKENS.lineHeight.body,
              fontWeight: interRegular?.fontWeight,
            },
          },
        },
      },

      // global fonts to update the material ui fonts
      typography: {
        // fontFamily: 'Inter', // Todo: Add this font
        fontFamily: FONT_STACK,
        fontSize: 16,

        // Remove styles for unsupported default variants.
        button: undefined,
        h1: undefined,
        h2: undefined,
        h3: undefined,
        h4: undefined,
        h5: undefined,
        subtitle1: undefined,
        subtitle2: undefined,

        // Deprecated Default MUI Typography Styles -- To be removed at a later date
        body1_deprecated: {
          color: DESIGN_SYSTEM_COLORS.text.primary,
          fontFamily: FONT_STACK,
          fontSize: '1rem',
          fontWeight: 400,
        },
        button_deprecated: {
          color: newDesignSystemColors.brandPrimary,
          fontFamily: FONT_STACK,
          fontSize: '1rem',
          fontWeight: 500,
        },
        h1_deprecated: {
          fontFamily: FONT_STACK,
          fontSize: '1.3rem',
          color: DESIGN_SYSTEM_COLORS.text.primary,
          paddingTop: '16px',
          paddingBottom: '16px',
          fontWeight: 'normal',
          [breakpoints.up('lg')]: {
            fontSize: '1.5rem',
          },
        },
        h2_deprecated: {
          fontSize: '1.25rem',
          fontFamily: FONT_STACK,
          fontWeight: 500,
        },
        h3_deprecated: {
          fontSize: '1.125rem',
          fontFamily: FONT_STACK,
          fontWeight: 500,
        },
        h4_deprecated: {
          fontSize: '1rem',
          fontFamily: FONT_STACK,
          fontWeight: 500,
        },
        h5_deprecated: {
          fontSize: '0.875rem',
          fontFamily: FONT_STACK,
          fontWeight: 500,
        },

        // DEPRECATED STYLES FROM PREVIOUS DESIGN SYSTEM VERSION
        header1_medium_deprecated: fontVariant(interMedium, '1.5rem', '1.3rem'),
        body_medium_deprecated: fontVariant(interMedium, '1rem', '1.5rem'),
        label_regular_deprecated: fontVariant(
          interRegular,
          '0.875rem',
          '1.225rem'
        ),
        caption_regular_deprecated: fontVariant(
          interRegular,
          '0.75rem',
          '1.4rem'
        ),
        caption_medium_deprecated: fontVariant(
          interMedium,
          '0.75rem',
          '1.125rem'
        ),

        button_new: {
          color: DESIGN_SYSTEM_COLORS.brand[600],
          fontFamily: FONT_STACK,
          fontSize: '1rem',
          fontWeight: 500,
        },

        // NEW STYLES FROM https://www.figma.com/design/tHuavQeTzVIG2XBlMfBhrh/Design-System-1.5?node-id=499-7685&m=dev
        oversize1_bold: fontVariant(
          interBold,
          FONT_SIZE.oversize[1],
          LINE_HEIGHT.oversize[1]
        ),
        oversize1_light: fontVariant(
          interLight,
          FONT_SIZE.oversize[1],
          LINE_HEIGHT.oversize[1]
        ),
        oversize2_regular: fontVariant(
          interRegular,
          FONT_SIZE.oversize[2],
          LINE_HEIGHT.oversize[2]
        ),
        oversize3_medium: fontVariant(
          interMedium,
          FONT_SIZE.oversize[3],
          LINE_HEIGHT.oversize[3]
        ),

        display1_semibold: fontVariant(
          interSemibold,
          FONT_SIZE.display[1],
          LINE_HEIGHT.display[1]
        ),
        display1_medium: fontVariant(
          interMedium,
          FONT_SIZE.display[1],
          LINE_HEIGHT.display[1]
        ),
        display1_regular: fontVariant(
          interRegular,
          FONT_SIZE.display[1],
          LINE_HEIGHT.display[1]
        ),
        display2_semibold: fontVariant(
          interSemibold,
          FONT_SIZE.display[2],
          LINE_HEIGHT.display[2]
        ),
        display2_medium: fontVariant(
          interMedium,
          FONT_SIZE.display[2],
          LINE_HEIGHT.display[2]
        ),
        display2_regular: fontVariant(
          interRegular,
          FONT_SIZE.display[2],
          LINE_HEIGHT.display[2]
        ),

        header1_medium: fontVariant(
          interMedium,
          FONT_SIZE.header[1],
          LINE_HEIGHT.header[1]
        ),
        header1_regular: fontVariant(
          interRegular,
          FONT_SIZE.header[1],
          LINE_HEIGHT.header[1]
        ),
        header2_semibold: fontVariant(
          interSemibold,
          FONT_SIZE.header[2],
          LINE_HEIGHT.header[2]
        ),
        header2_medium: fontVariant(
          interMedium,
          FONT_SIZE.header[2],
          LINE_HEIGHT.header[2]
        ),
        header2_regular: fontVariant(
          interRegular,
          FONT_SIZE.header[2],
          LINE_HEIGHT.header[2]
        ),

        body_medium: fontVariant(interMedium, FONT_SIZE.body, LINE_HEIGHT.body),
        body_regular: fontVariant(
          interRegular,
          FONT_SIZE.body,
          LINE_HEIGHT.body
        ),
        body_italic: {
          ...fontVariant(interRegular, FONT_SIZE.body, LINE_HEIGHT.body),
          fontStyle: 'italic',
        },
        label_medium: fontVariant(
          interMedium,
          FONT_SIZE.label,
          LINE_HEIGHT.label
        ),
        label_regular: fontVariant(
          interRegular,
          FONT_SIZE.label,
          LINE_HEIGHT.label
        ),
        // The small label pairs its own size with the body line height.
        label_small: fontVariant(
          interMedium,
          FONT_SIZE.smallLabel,
          LINE_HEIGHT.body
        ),
        caption_medium: fontVariant(
          interMedium,
          FONT_SIZE.caption,
          LINE_HEIGHT.caption
        ),
        caption_regular: fontVariant(
          interRegular,
          FONT_SIZE.caption,
          LINE_HEIGHT.caption
        ),
        overline_semibold: {
          ...fontVariant(
            interSemibold,
            FONT_SIZE.overline,
            LINE_HEIGHT.overline
          ),
          letterSpacing: '0.6px',
          textTransform: 'uppercase',
        },

        small_body_medium: fontVariant(
          interMedium,
          FONT_SIZE.smallBody,
          LINE_HEIGHT.smallBody
        ),
        small_body_regular: fontVariant(
          interRegular,
          FONT_SIZE.smallBody,
          LINE_HEIGHT.smallBody
        ),

        input_floating_label_regular: {
          ...fontVariant(
            interRegular,
            FONT_SIZE.floatingLabel,
            LINE_HEIGHT.floatingLabel
          ),
          color: DESIGN_SYSTEM_COLORS.text.secondary,
        },
      },
    });

    return {
      ...defaultTheme,
      components: {
        ...getMuiOverrides({ defaultTheme }),
        ...defaultTheme.components,
      },
    };
  };
  return createCustomTheme();
};

export default createBDTheme;
